import { Card, CryptoIcon, getNetworkName, TransactionTypeBadge } from '@valiant/ui-shared'
import { CheckCircle2, Clock, ExternalLink } from 'lucide-react'
import type { GetCompanyUtxoMempoolActivityQuery } from '../../../generated/graphql'
import { getUtxoTransactionUrl } from './utxoExplorer'

type UtxoMempoolActivity =
  GetCompanyUtxoMempoolActivityQuery['getCompanyUtxoMempoolActivity'][number]

function formatTimeAgo(dateString: string): string {
  const diffInSeconds = Math.max(0, Math.floor((Date.now() - new Date(dateString).getTime()) / 1000))

  if (diffInSeconds < 60) {
    return 'Just now'
  }
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return `${minutes} min ago`
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return `${hours} hour${hours > 1 ? 's' : ''} ago`
  }

  const days = Math.floor(diffInSeconds / 86400)
  return `${days} day${days > 1 ? 's' : ''} ago`
}

export function UtxoMempoolActivityItem({ activity }: { activity: UtxoMempoolActivity }) {
  const isConfirmed = activity.status === 'CONFIRMED'
  const StatusIcon = isConfirmed ? CheckCircle2 : Clock
  const shortenedHash = `${activity.transactionHash.slice(0, 8)}...${activity.transactionHash.slice(-8)}`
  const transactionUrl = getUtxoTransactionUrl(activity.chain, activity.transactionHash)

  const openTransaction = () => {
    if (transactionUrl) {
      window.open(transactionUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Card
      className={transactionUrl ? 'p-4 cursor-pointer hover:bg-background/5' : 'p-4'}
      role={transactionUrl ? 'link' : undefined}
      tabIndex={transactionUrl ? 0 : undefined}
      onClick={transactionUrl ? openTransaction : undefined}
      onKeyDown={
        transactionUrl
          ? (event) => {
              if (event.key === 'Enter') openTransaction()
            }
          : undefined
      }
    >
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center justify-between md:justify-start gap-4 flex-wrap w-full">
          <TransactionTypeBadge type="DEPOSIT" />

          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="flex items-center gap-2 w-full md:w-auto md:gap-3 flex-wrap">
              <div className="w-8 h-8 flex items-center justify-center flex-wrap">
                <CryptoIcon symbol={activity.currency} className="w-8 h-8" />
              </div>
              <div>
                <div className="font-medium text-sm">
                  {activity.amount} {activity.currency}
                </div>
                <div className="text-xs text-card-text font-mono">
                  {shortenedHash}:{activity.outputIndex}
                </div>
              </div>
              <div>
                <div className="text-xs">{activity.chain}</div>
                <div className="text-xs text-card-text">
                  {getNetworkName(activity.chain, activity.currency)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-8 flex-wrap md:flex-nowrap w-full md:w-auto justify-end md:justify-start md:shrink-0">
          <div className="flex items-center gap-2">
            <div
              className={`px-3 py-2 rounded text-xs font-medium border flex items-center justify-center gap-2 ${
                isConfirmed
                  ? 'bg-main-green-muted text-main-green border border-main-green'
                  : 'bg-main-orange-muted text-main-orange border border-main-orange'
              }`}
            >
              <StatusIcon className="h-3 w-3" />
              {isConfirmed ? 'Confirmation received' : 'Awaiting confirmation'}
            </div>
          </div>
          <div className="text-xs font-semibold text-right shrink-0">
            <div className="flex items-center gap-2">
              <div className="min-w-[80px]">{formatTimeAgo(activity.observedAt)}</div>
              {transactionUrl && <ExternalLink className="h-4 w-4 text-main" />}
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
